import React, { useState } from 'react';
import { WIDGET_DEFINITIONS } from './widgets/widgetRegistry';
import { useDashboard } from '../../contexts/DashboardContext';
import type { WidgetCategory, WidgetDefinition } from '../../types/dashboard';

interface WidgetCatalogProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES: { key: WidgetCategory | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'kpi', label: 'KPIs' },
  { key: 'chart', label: 'Graficos' },
  { key: 'list', label: 'Listas' },
  { key: 'calendar', label: 'Calendario' },
];

const WidgetCatalog: React.FC<WidgetCatalogProps> = ({ isOpen, onClose }) => {
  const { addWidget, layout, generateWidgetId } = useDashboard();
  const [activeCategory, setActiveCategory] = useState<WidgetCategory | 'all'>('all');

  if (!isOpen) return null;

  const existingTypes = new Set(layout.widgets.map((w) => w.type));

  const filtered = activeCategory === 'all'
    ? WIDGET_DEFINITIONS
    : WIDGET_DEFINITIONS.filter((d) => d.category === activeCategory);

  const handleAdd = (def: WidgetDefinition) => {
    // Find next available y position
    const maxY = layout.widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0);
    addWidget({
      i: generateWidgetId(),
      type: def.type,
      x: 0,
      y: maxY,
      w: def.defaultSize.w,
      h: def.defaultSize.h,
      config: { ...def.defaultConfig },
    });
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.2s ease-out',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        width: '90%', maxWidth: '640px', maxHeight: '80vh',
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur-strong)',
        WebkitBackdropFilter: 'var(--glass-blur-strong)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-2xl)',
        boxShadow: 'var(--shadow-2xl)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        animation: 'fadeIn 0.2s ease-out',
      }}>
        {/* Header */}
        <div style={{
          padding: 'var(--space-5)',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', margin: 0 }}>
              Adicionar Widget
            </h2>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', margin: 0, marginTop: '4px' }}>
              Escolha um widget para adicionar a sua dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--text-tertiary)', fontSize: '20px',
              cursor: 'pointer', padding: '4px', lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Category tabs */}
        <div style={{
          display: 'flex', gap: 'var(--space-1)',
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--border-light)',
          overflowX: 'auto',
        }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              style={{
                padding: '6px 14px',
                borderRadius: 'var(--radius-lg)',
                border: 'none',
                fontSize: 'var(--text-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                background: activeCategory === cat.key ? 'var(--color-primary-600)' : 'transparent',
                color: activeCategory === cat.key ? 'white' : 'var(--text-secondary)',
                whiteSpace: 'nowrap',
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Widget list */}
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4) var(--space-5)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--space-3)' }}>
            {filtered.map((def) => {
              const alreadyAdded = existingTypes.has(def.type);
              return (
                <button
                  key={def.type}
                  onClick={() => !alreadyAdded && handleAdd(def)}
                  disabled={alreadyAdded}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                    padding: 'var(--space-4)',
                    borderRadius: 'var(--radius-xl)',
                    border: '1px solid var(--glass-border)',
                    background: alreadyAdded ? 'rgba(100, 116, 139, 0.05)' : 'var(--glass-bg-subtle)',
                    cursor: alreadyAdded ? 'not-allowed' : 'pointer',
                    opacity: alreadyAdded ? 0.5 : 1,
                    transition: 'all 0.15s',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <span style={{ fontSize: '28px', flexShrink: 0 }}>{def.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 'var(--text-sm)', fontWeight: 600,
                      color: alreadyAdded ? 'var(--text-tertiary)' : 'var(--text-primary)',
                      marginBottom: '2px',
                    }}>
                      {def.name}
                      {alreadyAdded && <span style={{ fontSize: 'var(--text-xs)', fontWeight: 400, marginLeft: '6px' }}>(adicionado)</span>}
                    </div>
                    <div style={{
                      fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {def.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default WidgetCatalog;
